import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { C, fmt } from "../theme";
import VideoTrim from "../services/videoTrim";

const MIN_SEL_SEC = 1;

function fmtBytes(n) {
  if (!n || n <= 0) return "—";
  if (n >= 1024 * 1024 * 1024) return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(0)} MB`;
  return `${(n / 1024).toFixed(0)} KB`;
}

/**
 * Trim step between the GoPro download and Confirm & Certify. Recreational
 * clips are physically shortened on-device (native VideoTrim); tournament clips
 * upload the full evidence and record the selected span as audited highlight
 * metadata (trimMeta.mode === "highlight").
 */
export default function TrimScreen({ preloadedVideo, inTournament = false, tournamentName = "", onCancel, onProceed }) {
  const videoRef = useRef(null);
  const timelineRef = useRef(null);
  const draggingRef = useRef(null); // "start" | "end" | null

  const [durationSec, setDurationSec] = useState(0);
  const [startSec, setStartSec] = useState(0);
  const [endSec, setEndSec] = useState(0);
  const [currentSec, setCurrentSec] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  // Latest values for the pointer-move handler (avoids stale closures).
  const durRef = useRef(0);
  const startRef = useRef(0);
  const endRef = useRef(0);
  useEffect(() => { durRef.current = durationSec; }, [durationSec]);
  useEffect(() => { startRef.current = startSec; }, [startSec]);
  useEffect(() => { endRef.current = endSec; }, [endSec]);
  // GoPro MP4s store the moov atom at the END, so <video>.duration reads
  // Infinity until the browser seeks there. Track the forced-seek recovery.
  const durationHackRef = useRef(false);
  const resolvedRef = useRef(false);

  const previewUrl = preloadedVideo?.previewUrl || null;
  const originalBytes = preloadedVideo?.blob?.size || 0;
  const nativeAvailable = Capacitor.isNativePlatform();

  const selDur = Math.max(0, endSec - startSec);
  const isFullClip = durationSec > 0 && startSec <= 0.15 && endSec >= durationSec - 0.15;
  const estBytes = durationSec > 0 ? Math.round(originalBytes * (selDur / durationSec)) : originalBytes;

  const startPct = durationSec > 0 ? (startSec / durationSec) * 100 : 0;
  const endPct = durationSec > 0 ? (endSec / durationSec) * 100 : 100;
  const playPct = durationSec > 0 ? (currentSec / durationSec) * 100 : 0;

  const resolveDuration = () => {
    const el = videoRef.current;
    if (!el || resolvedRef.current) return;
    const d = el.duration;
    if (Number.isFinite(d) && d > 0) {
      resolvedRef.current = true;
      setDurationSec(d);
      setStartSec(0);
      setEndSec(d);
      if (durationHackRef.current) {
        durationHackRef.current = false;
        try { el.currentTime = 0; } catch (_) {}
        setCurrentSec(0);
      }
    }
  };

  const onLoadedMetadata = () => {
    const el = videoRef.current;
    if (!el) return;
    if (Number.isFinite(el.duration) && el.duration > 0) {
      resolveDuration();
    } else {
      // Force the browser to compute duration for moov-at-end MP4s.
      durationHackRef.current = true;
      try { el.currentTime = 1e101; } catch (_) {}
    }
  };

  const onTimeUpdate = () => {
    const el = videoRef.current;
    // Ignore the giant seek used to recover duration until it's resolved.
    if (!el || !resolvedRef.current) return;
    const t = el.currentTime;
    // Loop playback within the selected span.
    if (t >= endRef.current - 0.02) {
      el.pause();
      el.currentTime = startRef.current;
      setPlaying(false);
      setCurrentSec(startRef.current);
      return;
    }
    setCurrentSec(t);
  };

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      if (el.currentTime < startSec || el.currentTime >= endSec) el.currentTime = startSec;
      el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const pctFromClientX = (clientX) => {
    const el = timelineRef.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width));
  };

  const onPointerMove = useCallback((e) => {
    const which = draggingRef.current;
    const d = durRef.current;
    if (!which || !d) return;
    const el = timelineRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const t = pct * d;
    if (which === "start") {
      setStartSec(Math.min(t, endRef.current - MIN_SEL_SEC));
    } else {
      setEndSec(Math.max(t, startRef.current + MIN_SEL_SEC));
    }
  }, []);

  const onPointerUp = useCallback(() => {
    draggingRef.current = null;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  }, [onPointerMove]);

  const startDrag = (which) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = which;
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    // Move the playhead to the handle being grabbed for immediate feedback.
    const el = videoRef.current;
    if (el) {
      const target = which === "start" ? startRef.current : endRef.current;
      el.currentTime = target;
      setCurrentSec(target);
    }
  };

  useEffect(() => () => {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  }, [onPointerMove, onPointerUp]);

  const seekToClick = (e) => {
    if (draggingRef.current) return;
    const pct = pctFromClientX(e.clientX);
    const t = pct * durationSec;
    const el = videoRef.current;
    if (el) el.currentTime = t;
    setCurrentSec(t);
  };

  const buildTrimMeta = (mode) => ({
    mode,
    originalDurationMs: Math.round(durationSec * 1000),
    startMs: Math.round(startSec * 1000),
    endMs: Math.round(endSec * 1000),
    trimmedDurationMs: Math.round(selDur * 1000),
  });

  const proceedFull = () => onProceed?.({ preloadedVideo, trimMeta: null });

  const handlePrimary = async () => {
    const startMs = Math.round(startSec * 1000);
    const endMs = Math.round(endSec * 1000);
    // Tournament highlights are audited but still uploaded trimmed: only the
    // selected span is sent, with the original length + cut points recorded.
    const mode = inTournament ? "highlight" : "trimmed";

    // No reduction — send the clip as-is (nothing to trim).
    if (isFullClip) {
      onProceed?.({ preloadedVideo, trimMeta: null });
      return;
    }

    // Guard: if the preview's duration hasn't resolved yet the selection is
    // 0–0, which the native trimmer rejects. Ask the user to wait rather than error.
    if (!(durationSec > 0) || endMs - startMs < MIN_SEL_SEC * 1000) {
      setError("The video is still loading its length — wait a moment and try again, or use the full clip.");
      return;
    }

    const sourcePath = preloadedVideo?.sourcePath || preloadedVideo?.sourceUri || null;
    if (!nativeAvailable || !sourcePath) {
      // No native trimmer available — fall back to uploading the full clip with
      // the intended span recorded as metadata.
      onProceed?.({ preloadedVideo, trimMeta: buildTrimMeta(mode) });
      return;
    }

    setWorking(true);
    setError(null);
    try {
      const stem = String(preloadedVideo?.fileName || "clip").replace(/\.[^.]+$/, "");
      const outName = `${stem}_trim.mp4`;
      const res = await VideoTrim.trim({ path: sourcePath, startMs, endMs, outName });
      const uri = res?.uri || (res?.path ? `file://${res.path}` : null);
      let webSrc = uri;
      try { webSrc = Capacitor.convertFileSrc(uri); } catch (_) {}
      const resp = await fetch(webSrc);
      const blob = await resp.blob();
      const trimmedVideo = {
        ...preloadedVideo,
        blob,
        previewUrl: webSrc,
        mimeType: "video/mp4",
        fileName: outName,
        sourcePath: res?.path || sourcePath,
        sourceUri: uri,
      };
      onProceed?.({ preloadedVideo: trimmedVideo, trimMeta: buildTrimMeta(mode) });
    } catch (e) {
      setWorking(false);
      setError(`Couldn't shorten the clip: ${e?.message || e}. You can upload the full clip instead.`);
    }
  };

  const wrapStyle = {
    position: "absolute",
    inset: 0,
    background: C.deepWater,
    color: C.white,
    padding: "16px 14px 24px",
    boxSizing: "border-box",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    zIndex: 40,
  };
  const card = {
    background: C.cardBg,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: 13,
    marginBottom: 11,
  };
  const btn = (variant = "primary", disabled = false) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    width: "100%",
    padding: "13px 0",
    borderRadius: 11,
    border: variant === "ghost" ? `1px solid ${C.border}` : "none",
    background:
      variant === "ghost" ? "transparent" :
      variant === "gold" ? `linear-gradient(135deg, ${C.gold}, ${C.seafoam})` :
      `linear-gradient(135deg, ${C.accent}, ${C.teal})`,
    color: variant === "ghost" ? C.sand : variant === "gold" ? "#04121f" : "#fff",
    fontSize: 12,
    fontFamily: "Georgia, serif",
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.45 : 1,
    marginBottom: 9,
  });
  const handleStyle = (leftPct) => ({
    position: "absolute",
    top: -2,
    bottom: -2,
    left: `calc(${leftPct}% - 8px)`,
    width: 16,
    background: C.gold,
    borderRadius: 5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "ew-resize",
    zIndex: 4,
    touchAction: "none",
  });

  const primaryLabel = inTournament
    ? `\u2702 Trim Highlight & Certify \u00b7 ${fmt(Math.round(selDur))}`
    : `\u2702 Trim & Certify \u00b7 ${fmt(Math.round(selDur))}`;

  const frames = useMemo(() => Array.from({ length: 10 }, (_, i) => i), []);

  return (
    <div style={wrapStyle}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 10, letterSpacing: 3, color: C.teal, textTransform: "uppercase" }}>GoPro · Shorten Clip</div>
        <div style={{ fontFamily: "Georgia, serif", fontWeight: 700, fontSize: 20 }}>Trim Before Upload</div>
      </div>

      {inTournament && (
        <div style={{ ...card, background: "rgba(224,123,57,0.12)", border: `1px solid ${C.warning}80`, display: "flex", gap: 9, alignItems: "flex-start" }}>
          <span style={{ fontSize: 16 }}>{"\u{1F3C6}"}</span>
          <div style={{ fontSize: 11, lineHeight: 1.4, color: "#f0c19a" }}>
            <strong style={{ color: C.warning }}>Tournament mode{tournamentName ? ` · ${tournamentName}` : ""}</strong> — only the
            highlighted span you select is uploaded and certified. The original length and cut points are recorded on the certificate for audit.
          </div>
        </div>
      )}

      {/* Video preview */}
      <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", border: `1px solid ${C.border}`, marginBottom: 11, background: "linear-gradient(135deg,#12305c,#0a1c38)" }}>
        {previewUrl ? (
          <video
            ref={videoRef}
            src={previewUrl}
            playsInline
            onLoadedMetadata={onLoadedMetadata}
            onDurationChange={resolveDuration}
            onTimeUpdate={onTimeUpdate}
            onClick={togglePlay}
            style={{ width: "100%", maxHeight: 220, display: "block", background: "#000", objectFit: "contain" }}
          />
        ) : (
          <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 12 }}>
            No preview available
          </div>
        )}
        <button
          onClick={togglePlay}
          style={{
            position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)",
            width: 52, height: 52, borderRadius: "50%", background: "rgba(0,0,0,0.45)",
            border: "1px solid rgba(255,255,255,0.5)", color: "#fff", fontSize: 20, cursor: "pointer",
          }}
        >
          {playing ? "\u23F8" : "\u25B6"}
        </button>
        <span style={{ position: "absolute", right: 8, bottom: 8, fontSize: 9, background: "rgba(0,0,0,0.6)", padding: "2px 6px", borderRadius: 5, color: "#fff", fontFamily: "monospace" }}>
          {fmt(Math.round(currentSec))} / {fmt(Math.round(durationSec))}
        </span>
      </div>

      {/* Trim timeline */}
      <div style={card}>
        <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: C.muted, marginBottom: 9 }}>
          {inTournament ? "Highlight span (audited)" : "Trim selection"}
        </div>
        <div
          ref={timelineRef}
          onPointerDown={seekToClick}
          style={{ position: "relative", height: 58, borderRadius: 10, background: "#0a1c38", display: "flex", touchAction: "none", cursor: "pointer" }}
        >
          {/* filmstrip */}
          <div style={{ position: "absolute", inset: 0, borderRadius: 9, overflow: "hidden", display: "flex" }}>
            {frames.map((i) => (
              <div key={i} style={{ flex: 1, background: i % 2 ? "linear-gradient(135deg,#12315e,#0a1c3c)" : "linear-gradient(135deg,#173a6e,#0c2144)", borderRight: "1px solid rgba(3,10,22,0.6)" }} />
            ))}
          </div>
          {/* dimmed trimmed-away ends */}
          <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: `${startPct}%`, background: "rgba(3,7,14,0.7)", zIndex: 2 }} />
          <div style={{ position: "absolute", top: 0, bottom: 0, right: 0, width: `${100 - endPct}%`, background: "rgba(3,7,14,0.7)", zIndex: 2 }} />
          {/* selection border */}
          <div style={{ position: "absolute", top: -2, bottom: -2, left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%`, border: `2px solid ${C.gold}`, borderRadius: 9, zIndex: 3, boxSizing: "border-box" }} />
          {/* handles */}
          <div style={handleStyle(startPct)} onPointerDown={startDrag("start")}>
            <div style={{ width: 2, height: 22, background: "#04121f", borderRadius: 2 }} />
          </div>
          <div style={handleStyle(endPct)} onPointerDown={startDrag("end")}>
            <div style={{ width: 2, height: 22, background: "#04121f", borderRadius: 2 }} />
          </div>
          {/* playhead */}
          <div style={{ position: "absolute", top: -6, bottom: -6, left: `${playPct}%`, width: 2, background: "#fff", boxShadow: "0 0 6px rgba(255,255,255,0.85)", zIndex: 5, pointerEvents: "none" }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: C.muted, marginTop: 9, fontFamily: "monospace" }}>
          <span>0:00</span>
          <span>{fmt(Math.round(durationSec / 2))}</span>
          <span>{fmt(Math.round(durationSec))}</span>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          {[
            { k: "Start", v: fmt(Math.round(startSec)) },
            { k: "End", v: fmt(Math.round(endSec)) },
            { k: inTournament ? "Highlight" : "Selected", v: fmt(Math.round(selDur)), dur: true },
          ].map((ro) => (
            <div key={ro.k} style={{
              flex: 1, textAlign: "center", borderRadius: 10, padding: "8px 4px",
              background: ro.dur ? "linear-gradient(135deg,rgba(59,130,246,0.18),rgba(30,77,156,0.14))" : C.midnight,
              border: `1px solid ${ro.dur ? "rgba(96,165,250,0.5)" : C.border}`,
            }}>
              <div style={{ fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted }}>{ro.k}</div>
              <div style={{ fontSize: 15, fontWeight: 700, fontFamily: "monospace", marginTop: 3, color: ro.dur ? C.gold : C.white }}>{ro.v}</div>
            </div>
          ))}
        </div>

        <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.45, marginTop: 11 }}>
          {!nativeAvailable
            ? "On-device trimming isn't available here — the full clip will upload and your selected span is saved as metadata."
            : inTournament
              ? `Only the highlighted ${fmt(Math.round(selDur))} is uploaded & certified (~${fmtBytes(estBytes)} instead of ${fmtBytes(originalBytes)}). Original length & cut points are recorded for audit.`
              : `~${fmtBytes(estBytes)} will upload instead of ${fmtBytes(originalBytes)}. Trimmed ends stay on the GoPro SD card — nothing is deleted. Cuts snap to the nearest keyframe.`}
        </div>
      </div>

      {error && (
        <div style={{ ...card, background: "rgba(224,48,48,0.12)", border: `1px solid ${C.red}80`, color: "#f2b8b8", fontSize: 12, lineHeight: 1.4 }}>
          {error}
        </div>
      )}

      <button
        style={btn(inTournament ? "primary" : "gold", working || !(durationSec > 0) || (!inTournament && isFullClip))}
        disabled={working || !(durationSec > 0) || (!inTournament && isFullClip)}
        onClick={handlePrimary}
      >
        {working ? "Shortening clip…" : !(durationSec > 0) ? "Loading video…" : primaryLabel}
      </button>
      <button style={btn("ghost", working)} disabled={working} onClick={proceedFull}>
        Use full clip →
      </button>
      <button style={{ ...btn("ghost", working), marginBottom: 0, textTransform: "none", letterSpacing: 0, fontFamily: "inherit", fontWeight: 600, fontSize: 12 }} disabled={working} onClick={onCancel}>
        ← Back to clips
      </button>
    </div>
  );
}
